import classNames from 'classnames';
import { KeyboardEvent, useRef, useState } from 'react';
import { getAssetUrl } from '../../../utils/config';
import useClickOutside from '../../../utils/useClickOutside';
import './style.scss';

export type SelectOption = { label: string; value: string; icon?: string };

export type SelectProps = {
  name: string;
  value: string;
  options: SelectOption[];
  onChange: (newValue: string) => void;
  placeholder?: string;
  className?: string;
  hasError?: boolean;
  isClearable?: boolean;
  limitInputLength?: boolean;
};

const Select = ({
  name,
  value: fieldValue,
  options,
  onChange,
  placeholder,
  className,
  hasError,
  isClearable,
  limitInputLength,
  ...props
}: SelectProps) => {
  const [isOpen, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const hasIcons = !!options?.[0]?.icon;
  const selectedOption = options?.find(({ value }) => value === fieldValue) ?? null;
  const filteredOptions = options?.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));
  const showLabel = selectedOption && !search;
  const isSearchDisabled = options.length < 5;

  const closeDropdown = () => {
    setOpen(false);
    setSearch('');
    inputRef.current?.blur();
  };

  useClickOutside(containerRef, isOpen, closeDropdown);

  const changeOption = (value: SelectOption['value']) => {
    const isSameValue = value === selectedOption?.value;

    if (isClearable) {
      onChange(isSameValue ? '' : value);
    } else {
      onChange(isSameValue ? selectedOption?.value : value);
    }
  };

  const toggler = () => {
    if (isOpen) {
      closeDropdown();
    } else {
      inputRef.current?.focus();
      setOpen(true);
    }
  };

  const onKeyDownEffects = (e: KeyboardEvent<HTMLInputElement>) => {
    if (['Escape', 'Tab'].includes(e.key)) {
      closeDropdown();
    }
  };

  return (
    <div className={classNames('select', { 'select--has-icons': hasIcons })} onClick={toggler} ref={containerRef}>
      <div
        className={classNames(
          'select__control',
          { 'select__control--has-focus': isOpen },
          { 'select__control--has-icons': hasIcons },
          { 'select__control--has-error': hasError }
        )}
      >
        <div className="select__control-label" data-test-id="select-control-label">
          {showLabel && hasIcons && (
            <img className="select__control-label__icon" src={selectedOption?.icon} data-test-id="select-label-icon" />
          )}
          {showLabel && (
            <p className="select__control-label__label" data-test-id="select-control-label">
              {selectedOption?.label}
            </p>
          )}
          <input
            style={showLabel ? { width: 0, padding: 0, border: 'none' } : {}}
            type="text"
            value={search}
            name={name}
            onClick={e => e.stopPropagation()}
            className={classNames('select__control-label__input', className)}
            onKeyDown={onKeyDownEffects}
            onFocus={() => setOpen(true)}
            ref={inputRef}
            placeholder={placeholder}
            onChange={e => setSearch(e.target.value)}
            readOnly={isSearchDisabled}
            {...props}
          />
        </div>
        {isClearable && selectedOption?.value && (
          <img
            onClick={event => {
              onChange('');
              event.stopPropagation();
            }}
            className="select__remove-icon"
            src={getAssetUrl('common/remove.svg')}
          />
        )}
        <img className="select__control-arrow" src={getAssetUrl(`inputs/chevron${isOpen ? 'Up' : 'Down'}.svg`)} />
      </div>
      {isOpen && !!filteredOptions.length && (
        <div className="select__menu" data-test-id="select-options">
          {filteredOptions.map(o => (
            <div
              key={o.value}
              onClick={() => changeOption(o.value)}
              className={classNames('select__menu-option', {
                'select__menu-option--is-selected': o.value === selectedOption?.value,
                'select__menu-option--is-clearable': isClearable
              })}
              data-test-id="select-option"
            >
              {hasIcons && <img className="select__menu-option-icon" src={o.icon} />}
              <label
                className={classNames('select__menu-option-label', {
                  'select__menu-option-label--length-limiter': limitInputLength
                })}
              >
                {o.label}
              </label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Select;
